import { useEffect, useRef, useState } from "react";
import type { IconName } from "../../icons";
import IconButton from "../IconButton/IconButton";
import Text from "../Text/Text";
import styles from "./CopyToClipboard.css";

type Status = "idle" | "copied" | "error";

const getIconButton = (
  label: string,
): Record<
  Status,
  { icon: IconName; color: "neutral" | "success" | "error"; ariaLabel: string }
> => ({
  idle: {
    icon: "clipboard-copy",
    color: "neutral",
    ariaLabel: `${label} kopieren`,
  },
  copied: { icon: "check", color: "success", ariaLabel: `${label} kopiert` },
  error: {
    icon: "circle-alert",
    color: "error",
    ariaLabel: "Kopieren fehlgeschlagen",
  },
});

const getAnnouncement = (label: string): Record<Status, string> => ({
  idle: "",
  copied: `${label} kopiert`,
  error: "Kopieren fehlgeschlagen",
});

interface CopyToClipboardProps {
  value: string;
  /** Noun describing the copied value, e.g. "ID" or "Link" — used in aria-labels and the live announcement. */
  label: string;
}

const CopyToClipboard = ({ value, label }: CopyToClipboardProps) => {
  const [status, setStatus] = useState<Status>("idle");
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => () => clearTimeout(timeoutRef.current), []);

  const handleClick = async () => {
    let next: Status;
    try {
      await navigator.clipboard.writeText(value);
      next = "copied";
    } catch {
      next = "error";
    }
    clearTimeout(timeoutRef.current);
    setStatus(next);
    timeoutRef.current = setTimeout(() => setStatus("idle"), 2000);
  };

  const { icon, color, ariaLabel } = getIconButton(label)[status];

  return (
    <div className={styles.container({ status })}>
      <span className={styles.value} title={value}>
        <Text variant="label-large">{value}</Text>
      </span>
      <IconButton
        icon={icon}
        ariaLabel={ariaLabel}
        size="large"
        color={color}
        variant="secondary"
        onClick={() => void handleClick()}
      />
      <span className={styles.srOnly} role="status">
        {getAnnouncement(label)[status]}
      </span>
    </div>
  );
};

export default CopyToClipboard;
